import { memo } from 'react';
import { lt } from '@mews/framework';
import { SidebarProps, Button, Stack } from '@optimus-web/core';
import { useResolveRoute } from '@mews/routing';
import { RouteName } from '@mews-commander/enums';
import { ResourceBlockType, useRepositoryMutation } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { ResourceBlockResourceUse } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { CommanderSidebar } from '../../../components/sidebar';
import EntityTitle from '../../../components/commander-ui/EntityTitle';
import TemplateFetcher from '../../app/components/TemplateFetcher';
import { DialogBackendPage, useDialogBackendPage } from '../../app/components/DialogBackendPage';
import { DialogBackendPageDeleteAction } from '../../app/components/DialogBackendPageDeleteAction';
import { updateAfterHouseUseChange } from '../../timeline/actions';

interface ResourceBlockSimpleDetailProps {
    resourceUse: ResourceBlockResourceUse;
    hasEditButton: boolean;
}

const ResourceBlockSimpleDetail = memo(
    ({ resourceUse, hasEditButton }: ResourceBlockSimpleDetailProps) => {
        const resolveRoute = useResolveRoute();
        const { mutateAsync } = useRepositoryMutation('resourceBlocks', 'delete');

        const dispatch = useCommanderThunkDispatch();
        const handleSubmit = (data: ResourceBlockResourceUse, deleted = false) => {
            dispatch(updateAfterHouseUseChange(data, deleted));
        };

        const title = (
            <Stack distribution="space-between" alignment="center">
                <div>
                    {resourceUse.type === ResourceBlockType.InternalUse ? lt('HouseUse') : lt('OutOfOrder')}
                    {UnicodeEntity.NoBreakSpace}({resourceUse.name})
                </div>
                <DialogBackendPageDeleteAction
                    entityName={resourceUse.name ?? ''}
                    onDelete={async () =>
                        mutateAsync(
                            { resourceBlockIds: [resourceUse.id] },
                            {
                                onSuccess: () => {
                                    handleSubmit(resourceUse, true);
                                },
                            }
                        )
                    }
                />
            </Stack>
        );
        const params = { id: resourceUse.id };

        const [dialogProps, toggleDialog] = useDialogBackendPage({
            title,
            routeName: RouteName.ResourceBlockDetail,
            params,
            onSubmit: () => handleSubmit(resourceUse),
        });

        return (
            <>
                <Stack vertical spacing="200">
                    {hasEditButton && (
                        <Button onClick={toggleDialog} type="submit">
                            {lt('Edit')}
                        </Button>
                    )}
                    <TemplateFetcher url={resolveRoute(RouteName.ResourceBlockSimpleDetail, params)} layout="Empty" />
                </Stack>
                <DialogBackendPage {...dialogProps} />
            </>
        );
    },
    (prevProps, nextProps) =>
        prevProps.hasEditButton === nextProps.hasEditButton && prevProps.resourceUse.id === nextProps.resourceUse.id
);

const ResourceBlockSimpleDetailSidebar = CommanderSidebar(ResourceBlockSimpleDetail);

export default ({ resourceUse, ...other }: SidebarProps & ResourceBlockSimpleDetailProps) => (
    <ResourceBlockSimpleDetailSidebar
        title={<EntityTitle>{resourceUse.name}</EntityTitle>}
        resourceUse={resourceUse}
        {...other}
    />
);
